import React, { useState } from "react";
import {
    Badge,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    Menu,
    Typography,
    makeStyles,
    Avatar,
} from "@material-ui/core";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { client } from "../dataProvider";

const useStyles = makeStyles((theme) => ({
    menuPaper: {
        width: 400,
        maxWidth: "calc(100vw - 32px)",
        borderRadius: 10,
        overflow: "hidden",
    },
    header: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
    },
    list: {
        maxHeight: 440,
        overflowY: "auto",
        padding: 0,
    },
    item: {
        alignItems: "flex-start",
        padding: theme.spacing(1.75, 2),
        cursor: "pointer",
        transition: "background-color 120ms ease",
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    avatar: {
        width: theme.spacing(5),
        height: theme.spacing(5),
        marginRight: theme.spacing(1.5),
        marginTop: 2,
        fontSize: 15,
        fontWeight: 700,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        flexShrink: 0,
    },
    notificationBody: {
        minWidth: 0,
        flex: 1,
    },
    topRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: 2,
    },
    senderName: {
        fontWeight: 700,
        lineHeight: 1.25,
        color: theme.palette.text.primary,
    },
    date: {
        color: theme.palette.text.hint,
        whiteSpace: "nowrap",
        marginTop: 1,
        flexShrink: 0,
    },
    notificationTitle: {
        fontWeight: 600,
        lineHeight: 1.35,
        color: theme.palette.text.primary,
        marginTop: 2,
    },
    content: {
        marginTop: 4,
        color: theme.palette.text.secondary,
        lineHeight: 1.35,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    subtleMeta: {
        marginTop: theme.spacing(0.75),
        color: theme.palette.text.secondary,
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
    },
    title: {
        fontWeight: 700,
        lineHeight: 1.25,
    },
    stateBox: {
        padding: theme.spacing(4, 2),
        textAlign: "center",
        color: theme.palette.text.secondary,
    },
    errorIcon: {
        marginBottom: theme.spacing(1),
        color: theme.palette.error.main,
    },
    emptyIcon: {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.disabled,
    },
}));

const NOTIFICATIONS_QUERY_KEY = "recipient-notifications";

async function fetchNotifications() {
    const response = await client("recipient-notifications", {
        method: "GET",
    });

    return response.results || [];
}

async function markNotificationAsViewed(recipientNotificationId:any) {
    await client(
        `recipient-notifications/${recipientNotificationId}/view_notification`,
        {
            method: "GET",
        }
    );

    return true;
}

function formatNotificationDate(value:any) {
    if (!value) return "";

    return new Intl.DateTimeFormat("it-IT", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(value));
}

function getSenderFullName(sender:any) {
    if (!sender) return "Sistema";

    const name = sender.name || sender.first_name || "";
    const surname = sender.surname || sender.last_name || "";

    const fullName = `${name} ${surname}`.trim();

    return fullName || sender.username || "Utente";
}

function getSenderInitials(sender:any) {
    const fullName = getSenderFullName(sender);

    return fullName
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part:any) => part.charAt(0).toUpperCase())
        .join("");
}

function NotificationAvatar({ sender }:any) {
    const fullName = getSenderFullName(sender);

    return (
        <Avatar
            src={sender?.avatar || undefined}
            alt={fullName}
            className={useStyles().avatar}
        >
            {getSenderInitials(sender)}
        </Avatar>
    );
}

export default function NotificationMenu() {
    const classes = useStyles();
    const history = useHistory();
    const queryClient = useQueryClient();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const {
        data: notifications = [],
        isLoading,
        isFetching,
        isError,
    } = useQuery(
        NOTIFICATIONS_QUERY_KEY,
        fetchNotifications,
        {
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: true,
            staleTime: 15000,
        }
    );

    const markAsViewedMutation = useMutation(markNotificationAsViewed, {
        onSuccess: () => {
            queryClient.invalidateQueries(NOTIFICATIONS_QUERY_KEY);
        },
    });

    const unreadCount = notifications.length;

    const handleOpen = (event:any) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = async (recipientNotification:any) => {
        const redirect = recipientNotification.notification?.redirect;

        await markAsViewedMutation.mutateAsync(recipientNotification.id);

        handleClose();

        if (redirect) {
            history.push(redirect);
        }
    };

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleOpen}
                aria-label="Apri notifiche"
            >
                <Badge
                    badgeContent={unreadCount}
                    color="primary"
                    max={99}
                    overlap="rectangular"
                >
                    <NotificationsNoneIcon />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                classes={{
                    paper: classes.menuPaper,
                }}
            >
                <Box className={classes.header}>
                    <Box className={classes.titleRow}>
                        <Typography variant="subtitle1" className={classes.title}>
                            Notifiche
                        </Typography>

                        {isFetching && !isLoading && (
                            <CircularProgress size={16} />
                        )}
                    </Box>

                    <Typography variant="body2" color="textSecondary">
                        {unreadCount === 1
                            ? "Hai 1 notifica non letta"
                            : `Hai ${unreadCount} notifiche non lette`}
                    </Typography>
                </Box>

                <Divider />

                {isLoading && (
                    <Box className={classes.stateBox}>
                        <CircularProgress size={26} />
                        <Typography variant="body2" style={{ marginTop: 12 }}>
                            Caricamento notifiche...
                        </Typography>
                    </Box>
                )}

                {!isLoading && isError && (
                    <Box className={classes.stateBox}>
                        <ErrorOutlineIcon className={classes.errorIcon} />
                        <Typography variant="body2">
                            Non riesco a caricare le notifiche.
                        </Typography>
                    </Box>
                )}

                {!isLoading && !isError && notifications.length === 0 && (
                    <Box className={classes.stateBox}>
                        <DoneAllIcon className={classes.emptyIcon} />
                        <Typography variant="body2">
                            Nessuna nuova notifica.
                        </Typography>
                    </Box>
                )}

                {!isLoading && !isError && notifications.length > 0 && (
                    <List className={classes.list}>
                        {notifications.map((recipientNotification:any, index:any) => {
                            const notification = recipientNotification.notification;
                            const sender = notification?.sender;
                            const senderName = getSenderFullName(sender);

                            return (
                                <React.Fragment key={recipientNotification.id}>
                                    <ListItem
                                        button
                                        className={classes.item}
                                        disabled={markAsViewedMutation.isLoading}
                                        onClick={() =>
                                            handleNotificationClick(recipientNotification)
                                        }
                                    >


                                        <Box className={classes.notificationBody}>
                                            <Box className={classes.topRow}>
                                                <Typography
                                                    variant="body2"
                                                    className={classes.senderName}
                                                    noWrap
                                                >
                                                    {senderName}
                                                </Typography>

                                                {notification?.date && (
                                                    <Typography
                                                        variant="caption"
                                                        className={classes.date}
                                                    >
                                                        {formatNotificationDate(notification.date)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                className={classes.notificationTitle}
                                            >
                                                {notification?.title || "Notifica"}
                                            </Typography>

                                            {notification?.content && (
                                                <Typography
                                                    variant="body2"
                                                    className={classes.content}
                                                >
                                                    {notification.content}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ListItem>

                                    {index < notifications.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Menu>
        </>
    );
}